import { createFileRoute } from "@tanstack/react-router";
import {
  financeTransactionCSV,
  financeTransactionPDF,
} from "@/components/finance-transaction/finance-transaction-export";
import { FinanceTransactionTableColumns } from "@/components/finance-transaction/finance-transaction-meta";
import { FinanceTransactionModals } from "@/components/finance-transaction/finance-transaction-modals";
import FullPagination from "@/components/ui/shared/full-pagination";
import SimpleTable, { TableExports } from "@/components/ui/table";
import { TableAttributesModal } from "@/components/ui/table-attributes-modal";
import { useTableAttributes } from "@/hooks/use-table-attributes";
import { useTranslate } from "@/hooks/use-translate";
import useAppStore from "@/store";

import type { FinanceAccount } from "@/store/FinanceAccount";
import { ChevronLeft } from "lucide-react";
import { useEffect } from "react";

export const Route = createFileRoute("/(dasboard)/finance/accounts/$id")({
  component: FinanceAccountDetails,
});

function FinanceAccountDetails() {
  const params = Route.useParams();
  const store = useAppStore();
  const tableName = "finance_transactions";
  const tableAttributes = useTableAttributes();

  const financeAccount = store.financeAccount.current as FinanceAccount;
  const { t } = useTranslate();

  const search = ({ page = 1, per_page = 10 } = {}) => {
    store.financeTransaction.fetch({ page, per_page });
  };

  useEffect(() => {
    const id = params.id;
    if (id && store.auth.session) {
      store.financeAccount.fetchOne(id).then((account: FinanceAccount) => {
        store.financeAccount.setCurrent(account);
      });

      store.financeTransaction.fetch({
        finance_account_id: id,
      });
    }

    tableAttributes.loadAttributes(tableName, [
      "credit",
      "debit",
      "account",
      "status",
      "created",
      "category",
      "id",
      "reference",
      "actions",
    ]);
  }, [params.id, store.auth.session]);

  const columns = FinanceTransactionTableColumns({
    store,
    onView: (tx: any) => {
      store.financeTransaction.setCurrent(tx);
      store.display.show("view_finance_transaction");
    },
    onEdit: (tx: any) => {
      store.financeTransaction.setCurrent(tx);
      store.display.show("edit_finance_transaction");
    },
    onDelete: async (tx: any) => {
      store.financeTransaction.setCurrent(tx);
      store.display.show("delete_finance_transaction");
    },
  }).filter((item: any) =>
    (tableAttributes.attributes[tableName] ?? []).includes(item.name)
  );

  if (!financeAccount) return undefined;

  return (
    <section className="max-w-6xl mx-auto">
      <FinanceTransactionModals />
      <TableAttributesModal
        tableName={tableName}
        columns={FinanceTransactionTableColumns({ store })}
      />

      <div>
        <button
          type="button"
          onClick={() => window.history.back()}
          className="py-2 inline-flex gap-2 items-center justify-center rounded-lg text-sm text-gray-500 hover:text-gray-900 hover:underline font-semibold"
        >
          <ChevronLeft className="h-4 w-4" />
          <span className="">{t("Back")}</span>
        </button>
      </div>
      <header className="mt-2 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-xl">{financeAccount.name}</h2>
        <div className="bg-gray-50 flex">
          <div className="bg-gray-200 px-6 py-2 text-gray-800 rounded-l-full">
            {t("Solde")}
          </div>
          <div className="font-semibold px-6 py-2 bg-secondary-800 text-white rounded-r-full">
            {store.shop.formatAmount(financeAccount.balance)}
          </div>
        </div>
      </header>
      <div className="mt-6 flex items-center justify-between">
        <header className="">
          <h2 className="font-semibold">{t("Transactions history")}</h2>
        </header>
        <div className="flex items-center gap-2">
          <TableExports
            pdf={() =>
              financeTransactionPDF(
                columns,
                store.financeTransaction.pagination.data,
                t
              )
            }
            csv={() =>
              financeTransactionCSV(
                columns,
                store.financeTransaction.pagination.data
              )
            }
          />
        </div>
      </div>

      <aside className="mt-2">
        <SimpleTable
          items={store.financeTransaction.pagination.data}
          columns={columns}
        />

        <FullPagination
          className="mt-4"
          canEdit={true}
          pagination={store.financeTransaction.pagination}
          onGoto={(page: number) => search({ page })}
          onPerPage={(per_page: any) => search({ per_page })}
        />
      </aside>
    </section>
  );
}
